import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { WalkList, WalkListAddress } from '../../types/walklist.types';
import { toast } from 'react-hot-toast';
import {
  Map,
  List,
  MapPin,
  CheckCircle,
  XCircle,
  Clock,
  Home,
  ChevronRight,
  Filter,
  Plus,
  Eye,
  Folder
} from 'lucide-react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

type ViewMode = 'list' | 'map';

const WalkListsPage: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  const { selectedProjectId, selectedProject } = useProject();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [walkLists, setWalkLists] = useState<WalkList[]>([]);
  const [selectedWalkList, setSelectedWalkList] = useState<WalkList | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (selectedProjectId) {
      loadWalkLists();
    }
  }, [selectedProjectId]);

  const loadWalkLists = async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'walkLists'),
        where('projectId', '==', selectedProjectId)
      );
      const snapshot = await getDocs(q);
      const lists = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as WalkList));
      setWalkLists(lists);
    } catch (error) {
      console.error('Error loading walk lists:', error);
      // If no walk lists exist yet, just set empty array
      setWalkLists([]);
    } finally {
      setLoading(false);
    }
  };

  const updateAddressStatus = async (
    walkListId: string,
    addressId: string,
    status: WalkListAddress['status']
  ) => {
    try {
      const walkList = walkLists.find(wl => wl.id === walkListId);
      if (!walkList) return;

      const updatedAddresses = walkList.addresses.map(addr =>
        addr.id === addressId
          ? {
              ...addr,
              status,
              visitedAt: status !== 'pending' ? Timestamp.now() : undefined,
              visitedBy: status !== 'pending' ? currentUser?.uid : undefined,
              attemptCount: addr.attemptCount + 1
            }
          : addr
      );

      const visited = updatedAddresses.filter(a => a.status !== 'pending').length;
      const leadsCaptures = updatedAddresses.filter(a => a.leadId).length;

      await updateDoc(doc(db, 'walkLists', walkListId), {
        addresses: updatedAddresses,
        progress: {
          total: updatedAddresses.length,
          visited,
          leadsCaptures
        },
        updatedAt: Timestamp.now()
      });

      // Update local state
      setWalkLists(prev =>
        prev.map(wl =>
          wl.id === walkListId
            ? {
                ...wl,
                addresses: updatedAddresses,
                progress: { total: updatedAddresses.length, visited, leadsCaptures }
              }
            : wl
        )
      );

      toast.success('Address status updated');
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error('Failed to update address');
    }
  };

  const getStatusColor = (status: WalkListAddress['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'visited':
        return 'bg-blue-100 text-blue-800';
      case 'not_home':
        return 'bg-yellow-100 text-yellow-800';
      case 'refused':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: WalkListAddress['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'visited':
        return <Eye className="h-4 w-4 text-blue-600" />;
      case 'not_home':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'refused':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Home className="h-4 w-4 text-gray-400" />;
    }
  };

  const filteredWalkLists = statusFilter === 'all'
    ? walkLists
    : walkLists.filter(wl => wl.status === statusFilter);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Walk Lists</h1>
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded-md text-sm ${
                viewMode === 'list' ? 'bg-white shadow-sm' : ''
              }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-1 rounded-md text-sm ${
                viewMode === 'map' ? 'bg-white shadow-sm' : ''
              }`}
            >
              <Map className="h-4 w-4" />
            </button>
          </div>

          <button className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Walk List
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 mb-6">
        {/* Project indicator */}
        {selectedProject && (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md">
            <Folder className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">{selectedProject.name}</span>
          </div>
        )}

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-md"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">Loading walk lists...</p>
        </div>
      ) : walkLists.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <MapPin className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Walk Lists Yet</h3>
          <p className="text-gray-500 mb-4">
            Create your first walk list to start assigning territories to field staff.
          </p>
          <button className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Walk List
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="grid gap-4">
          {filteredWalkLists.map(walkList => (
            <div
              key={walkList.id}
              className="bg-white rounded-lg shadow p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{walkList.name}</h3>
                  <p className="text-sm text-gray-500">
                    {walkList.territory?.name || 'No territory assigned'}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    walkList.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : walkList.status === 'completed'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {walkList.status}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>
                    {walkList.progress.visited} / {walkList.progress.total} addresses
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${
                        walkList.progress.total > 0
                          ? (walkList.progress.visited / walkList.progress.total) * 100
                          : 0
                      }%`
                    }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {walkList.progress.total}
                  </p>
                  <p className="text-xs text-gray-500">Total Addresses</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {walkList.progress.visited}
                  </p>
                  <p className="text-xs text-gray-500">Visited</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600">
                    {walkList.progress.leadsCaptures}
                  </p>
                  <p className="text-xs text-gray-500">Leads Captured</p>
                </div>
              </div>

              {/* Assigned Users */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">Assigned to:</span>
                  {walkList.assignedToNames?.map((name, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-gray-100 rounded-full text-xs mr-1"
                    >
                      {name}
                    </span>
                  )) || <span className="text-sm text-gray-400">Unassigned</span>}
                </div>
                <button
                  onClick={() => setSelectedWalkList(walkList)}
                  className="flex items-center text-indigo-600 hover:text-indigo-800"
                >
                  View Details
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
            <div className="text-center">
              <Map className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Map View</h3>
              <p className="text-gray-500">
                Google Maps integration coming soon.
                <br />
                Connect your Google Maps API key in Settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Walk List Detail Modal */}
      {selectedWalkList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{selectedWalkList.name}</h2>
                <button
                  onClick={() => setSelectedWalkList(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {selectedWalkList.addresses.map(address => (
                  <div
                    key={address.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center">
                      {getStatusIcon(address.status)}
                      <div className="ml-3">
                        <p className="font-medium">{address.street}</p>
                        <p className="text-sm text-gray-500">
                          {address.city}, {address.state} {address.zipCode}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(address.status)}`}>
                        {address.status.replace('_', ' ')}
                      </span>
                      <select
                        value={address.status}
                        onChange={(e) =>
                          updateAddressStatus(
                            selectedWalkList.id,
                            address.id,
                            e.target.value as WalkListAddress['status']
                          )
                        }
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="pending">Pending</option>
                        <option value="visited">Visited</option>
                        <option value="not_home">Not Home</option>
                        <option value="refused">Refused</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalkListsPage;
